import type { ActivityEventType, ActivityResource } from "@/types/fleet";

/**
 * The browser end of the activity log.
 *
 * A module-level queue rather than component state, for the same reason
 * `lib/notifications/dismissed-store.ts` is one: the things that emit events are scattered across
 * views, client API modules and the layout, and none of them should have to hold a reference to
 * anything. `logActivity()` is a fire-and-forget call that never throws, never awaits and never
 * surfaces a toast — the log is telemetry about an action, and it must not be able to break the
 * action it describes. Every failure path here ends in a silent drop.
 *
 * Identity is deliberately absent from the payload. `user_id`, `user_name`, `client_id`,
 * `ip_address` and `occurred_at` are all derived upstream from the bearer token and the request;
 * the browser only gets to say *what* happened, never *who* it was.
 */

/** Beyond this the oldest events are dropped — a stalled upstream must not grow memory forever. */
const MAX_QUEUE = 100;

/** Upstream rejects a larger batch (`batch_too_large`), so the flush chunks to this. */
const MAX_BATCH = 50;

/** Long enough to coalesce a burst of navigations, short enough to survive an abrupt close. */
const FLUSH_DEBOUNCE_MS = 2_000;

/** A burst this size flushes immediately rather than waiting out the debounce. */
const FLUSH_AT = 20;

const ENDPOINT = "/api/activity";

export interface ActivityDraft {
  eventType: ActivityEventType;
  resource: ActivityResource;
  resourceId?: string | null;
  label: string;
  detail?: Record<string, string | number | null | undefined>;
  /** Defaults to the current route. */
  path?: string;
}

interface QueuedEvent {
  event_type: ActivityEventType;
  resource: ActivityResource;
  resource_id: string;
  label: string;
  path: string;
  detail: Record<string, string>;
  /** Advisory only — upstream stamps the authoritative `occurred_at`. */
  client_time: string;
}

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let enabled = false;
let listenersBound = false;

/**
 * Gate on the session. Without this every emit before login — and every one after logout — would
 * post to an endpoint that answers 401, for nothing.
 */
export function setActivityLoggingEnabled(next: boolean) {
  if (enabled === next) return;
  enabled = next;
  if (!enabled) {
    queue = [];
    clearTimer();
  }
}

function clearTimer() {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

/** Detail values travel as strings; anything unset is dropped rather than sent as "undefined". */
function normalizeDetail(detail: ActivityDraft["detail"]): Record<string, string> {
  const out: Record<string, string> = {};
  if (!detail) return out;
  for (const [key, value] of Object.entries(detail)) {
    if (value === null || value === undefined || value === "") continue;
    out[key] = String(value);
  }
  return out;
}

/**
 * Back/forward navigation and React's double-invoked effects both re-fire a `page_view` for the
 * route already at the tail of the queue. Collapsing them here keeps the log readable — a user
 * who sat on Tracking for an hour should not produce a page of identical rows.
 */
function isDuplicatePageView(event: QueuedEvent): boolean {
  if (event.event_type !== "page_view") return false;
  const last = queue[queue.length - 1];
  return last?.event_type === "page_view" && last.path === event.path;
}

export function logActivity(draft: ActivityDraft): void {
  if (!enabled || typeof window === "undefined") return;

  const event: QueuedEvent = {
    event_type: draft.eventType,
    resource: draft.resource,
    resource_id: draft.resourceId ?? "",
    label: draft.label,
    path: draft.path ?? window.location.pathname,
    detail: normalizeDetail(draft.detail),
    client_time: new Date().toISOString(),
  };

  if (isDuplicatePageView(event)) return;

  queue.push(event);
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);

  bindUnloadListeners();

  if (queue.length >= FLUSH_AT) {
    flushActivity();
    return;
  }

  if (timer === null) {
    timer = setTimeout(() => {
      timer = null;
      flushActivity();
    }, FLUSH_DEBOUNCE_MS);
  }
}

/**
 * Sends whatever is queued and forgets it, batch or no batch.
 *
 * `useBeacon` is the page-is-going-away path: `sendBeacon` survives the document being torn down,
 * which a normal `fetch` does not. It sends `text/plain`, so the proxy handler reads the raw body
 * rather than relying on the content type — see `app/api/activity/route.ts`.
 */
export function flushActivity(useBeacon = false): void {
  clearTimer();
  if (!enabled || queue.length === 0 || typeof window === "undefined") return;

  const pending = queue;
  queue = [];

  for (let i = 0; i < pending.length; i += MAX_BATCH) {
    const body = JSON.stringify({ events: pending.slice(i, i + MAX_BATCH) });

    if (useBeacon && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      try {
        if (navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "text/plain" }))) continue;
      } catch {
        // Fall through to fetch below.
      }
    }

    // `keepalive` so a flush that races a navigation still completes. Errors are swallowed on
    // purpose: a dropped activity event is not worth a console entry, let alone a user-visible one.
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => undefined);
  }
}

/**
 * Bound lazily on the first emit rather than at import time, so importing this module from a
 * server component stays inert and no listener exists for a session that never logs anything.
 */
function bindUnloadListeners() {
  if (listenersBound || typeof window === "undefined") return;
  listenersBound = true;

  // `visibilitychange` is the one that actually fires on mobile; `pagehide` covers bfcache.
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushActivity(true);
  });
  window.addEventListener("pagehide", () => flushActivity(true));
}

/**
 * The create/update/delete shorthand.
 *
 * CRUD is instrumented in the `lib/api/*.ts` clients rather than in the views that call them:
 * every write for a resource funnels through one function there, which already knows the resource
 * name and the id, whereas the views would need the same three lines repeated in a dozen dialog
 * save handlers. Emit sits after the `await`, so a write that failed is never logged as one that
 * happened.
 */
export function logCrud(
  eventType: Extract<ActivityEventType, "create" | "update" | "delete">,
  resource: ActivityResource,
  label: string,
  resourceId?: string | null,
): void {
  logActivity({ eventType, resource, resourceId, label });
}

/**
 * The `{ dateFrom, dateTo }` pair every report, history query and export carries.
 *
 * ISO rather than the user's locale format on purpose — an admin reading the log a month later
 * should not have to work out which of two ambiguous numbers was the day.
 */
export function rangeDetail(range: { from: Date; to: Date }): {
  dateFrom: string;
  dateTo: string;
} {
  return { dateFrom: range.from.toISOString(), dateTo: range.to.toISOString() };
}
